// Re:Focus — Capture Module
// Quick capture, NL parser, API fallback, capture modal logic

import { e, confidenceGlyph } from './utils.js';
import { PRESET_COLORS } from './state.js';

let _getAuthHeader = () => ({});
let _getBoardData = () => null;
let _getOverrides = () => ({});
let _saveOverrides = (ov) => {};
let _renderFromState = () => {};
let _mergeOverrides = (data) => data;
let _loadOverrides = () => ({});
let _trapFocus = () => () => {};
let _activeFocusTrapRef = { value: null };
let _focusTrapTriggerRef = { value: null };
let _AI_NAME = 'Cap';

export function initCapture({ getAuthHeader, getBoardData, getOverrides, saveOverrides, renderFromState, mergeOverrides, loadOverrides, trapFocus, activeFocusTrapRef, focusTrapTriggerRef, getAiName }) {
  _getAuthHeader = getAuthHeader;
  _getBoardData = getBoardData;
  _getOverrides = getOverrides;
  _saveOverrides = saveOverrides;
  _renderFromState = renderFromState;
  _mergeOverrides = mergeOverrides;
  _loadOverrides = loadOverrides;
  _trapFocus = trapFocus;
  _activeFocusTrapRef = activeFocusTrapRef;
  _focusTrapTriggerRef = focusTrapTriggerRef;
  if (getAiName) _AI_NAME = getAiName();
}

// Ripe flag state for capture
let _captureRipe = false;

// Current parse state for Quick tab
let _captureParseResult = null;
let _captureParseTimer = null;
let _captureCapPollTimer = null;
let _captureCapParseId = null;
let _captureCapFireTimer = null;
let _capParseAttempts = 0;
const MAX_PARSE_ATTEMPTS = 30;
let _capParseRequestText = '';

export function getColumnNames() {
  const boardData = _getBoardData();
  if (!boardData) return [];
  const data = _mergeOverrides(boardData);
  const ov = _loadOverrides();
  return data.columns
    .filter(col => !(ov.archivedColumns && ov.archivedColumns.includes(col.id)))
    .map(col => ({
      id: col.id,
      name: (ov.columnRenames && ov.columnRenames[col.id]) || col.name,
    }));
}

export function parseNaturalInput(text) {
  const result = { taskText: null, cardName: null, dueDate: null, context: null, column: null, columnHint: null, tasks: null, confidence: 'low' };
  if (!text || !text.trim()) return result;
  let cleaned = text.trim();

  // Extract @context [column-name] — route to a column
  const cols = getColumnNames();
  const ctxColMatch = cleaned.match(/@context\s+([^\n,@]+?)(?=\s+(?:add|with|tasks?:|,|$)|$)/i);
  if (ctxColMatch) {
    const query = ctxColMatch[1].toLowerCase();
    const match = cols.find(c => c.name.toLowerCase() === query)
      || cols.find(c => c.name.toLowerCase().startsWith(query));
    if (match) {
      result.column = match.id;
      result.columnHint = match.name;
    } else {
      result.columnHint = 'Column not found';
    }
    cleaned = cleaned.replace(/@context\s+[^\n,@]+?(?=\s+(?:add|with|tasks?:|,|$)|$)/i, '').trim();
  }

  // Extract multi-task: "with tasks:" or "tasks:" followed by comma-separated list
  const tasksMatch = cleaned.match(/(?:with\s+)?tasks?:\s*(.+)$/i);
  if (tasksMatch) {
    result.tasks = tasksMatch[1].split(',').map(t => t.trim()).filter(Boolean);
    cleaned = cleaned.replace(/(?:with\s+)?tasks?:\s*.+$/i, '').trim();
  }

  // Cue flag detection: @quick, @cue, or standalone "quick" → set cue flag and strip
  if (/@(?:quick|cue)\b/i.test(cleaned)) {
    result.cue = true;
    cleaned = cleaned.replace(/@(?:quick|cue)\b/i, '').trim();
  }
  if (/\bquick\b/i.test(cleaned)) {
    result.cue = true;
    cleaned = cleaned.replace(/\bquick\b/i, '').trim();
  }

  // Extract @tag → context (must come after @context and @cue extraction)
  const tagMatch = cleaned.match(/\s*@(\w+)\s*$/);
  if (tagMatch) {
    result.context = '@' + tagMatch[1].toLowerCase();
    cleaned = cleaned.replace(/\s*@\w+\s*$/, '').trim();
  }

  // Date patterns
  const today = new Date();
  const dayNames = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
  const dayAbbrevs = ['sun','mon','tue','wed','thu','fri','sat'];
  const monthNames = ['january','february','march','april','may','june','july','august','september','october','november','december'];
  const monthAbbrevs = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];

  function nextWeekday(dayIdx) {
    const d = new Date(today);
    const diff = (dayIdx - d.getDay() + 7) % 7 || 7;
    d.setDate(d.getDate() + diff);
    return d;
  }
  function fmtDate(d) {
    return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
  }

  // "tomorrow"
  if (/\btomorrow\b/i.test(cleaned)) {
    const d = new Date(today); d.setDate(d.getDate() + 1);
    result.dueDate = fmtDate(d);
    cleaned = cleaned.replace(/\btomorrow\b/i, '').trim();
  }
  // "today"
  else if (/\btoday\b/i.test(cleaned)) {
    result.dueDate = fmtDate(today);
    cleaned = cleaned.replace(/\btoday\b/i, '').trim();
  }
  // "next week"
  else if (/\bnext\s+week\b/i.test(cleaned)) {
    const d = new Date(today); d.setDate(d.getDate() + 7);
    result.dueDate = fmtDate(d);
    cleaned = cleaned.replace(/\bnext\s+week\b/i, '').trim();
  }
  // "this Friday", "next Monday", "before Wednesday"
  else {
    const dayPattern = new RegExp('\\b(?:this|next|before)\\s+(' + dayNames.join('|') + '|' + dayAbbrevs.join('|') + ')\\b', 'i');
    const dayMatch = cleaned.match(dayPattern);
    if (dayMatch) {
      const dayStr = dayMatch[1].toLowerCase();
      let dayIdx = dayNames.indexOf(dayStr);
      if (dayIdx === -1) dayIdx = dayAbbrevs.indexOf(dayStr);
      if (dayIdx !== -1) {
        result.dueDate = fmtDate(nextWeekday(dayIdx));
        cleaned = cleaned.replace(dayPattern, '').trim();
      }
    }
  }

  // "[Month] [D]" or "[Month] [D]st/nd/rd/th" — e.g. "April 10", "Apr 10th"
  if (!result.dueDate) {
    const monthPattern = new RegExp('\\b(' + monthNames.join('|') + '|' + monthAbbrevs.join('|') + ')\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b', 'i');
    const monthMatch = cleaned.match(monthPattern);
    if (monthMatch) {
      const mStr = monthMatch[1].toLowerCase();
      let mIdx = monthNames.indexOf(mStr);
      if (mIdx === -1) mIdx = monthAbbrevs.indexOf(mStr);
      if (mIdx !== -1) {
        const day = parseInt(monthMatch[2], 10);
        let year = today.getFullYear();
        // If month has already passed this year, use next year
        if (mIdx < today.getMonth() || (mIdx === today.getMonth() && day < today.getDate())) {
          year++;
        }
        result.dueDate = year + '-' + String(mIdx+1).padStart(2,'0') + '-' + String(day).padStart(2,'0');
        cleaned = cleaned.replace(monthPattern, '').trim();
      }
    }
  }

  // Strip trailing "due" or "before" left over after date extraction
  cleaned = cleaned.replace(/\b(?:due|before)\s*$/i, '').trim();

  // Column match (if not already matched by @context)
  if (!result.column) {
    for (const col of cols) {
      if (cleaned.toLowerCase().includes(col.name.toLowerCase())) {
        result.column = col.id;
        break;
      }
    }
  }

  // Action verb heuristic
  const actionVerbs = /^(email|call|send|draft|review|check|book|follow|schedule|write|update|find|research|add)\b/i;
  const hasVerb = actionVerbs.test(cleaned);

  if (hasVerb) {
    result.taskText = cleaned;
  } else {
    result.cardName = cleaned;
  }

  // Confidence
  const hasDateOrTag = !!(result.dueDate || result.context);
  const hasColumn = !!result.column;
  const hasTasks = result.tasks && result.tasks.length > 0;
  if ((hasVerb || hasTasks) && (hasDateOrTag || hasColumn)) result.confidence = 'high';
  else if (hasVerb || hasDateOrTag || hasColumn || hasTasks) result.confidence = 'medium';
  else result.confidence = 'low';

  return result;
}

function renderParsePreview(parsed) {
  const preview = document.getElementById('capture-parse-preview');
  const hint = document.getElementById('capture-parse-hint');
  if (!parsed || (!parsed.taskText && !parsed.cardName)) {
    preview.classList.add('hidden');
    preview.innerHTML = '';
    hint.classList.remove('hidden');
    return;
  }
  hint.classList.add('hidden');
  preview.classList.remove('hidden');

  let html = '';
  if (parsed.taskText) {
    html += `<div class="parse-preview-row"><span class="parse-label">Task</span><span class="parse-value">${e(parsed.taskText)}</span></div>`;
  }
  if (parsed.cardName) {
    html += `<div class="parse-preview-row"><span class="parse-label">Card</span><span class="parse-value">${e(parsed.cardName)}</span></div>`;
  }
  if (parsed.dueDate) {
    const d = new Date(parsed.dueDate + 'T00:00:00');
    const display = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    html += `<div class="parse-preview-row"><span class="parse-label">Due</span><span class="parse-value">${e(display)}</span></div>`;
  }
  if (parsed.context) {
    html += `<div class="parse-preview-row"><span class="parse-label">Context</span><span class="parse-value">${e(parsed.context)}</span></div>`;
  }
  if (parsed.columnHint) {
    const isError = parsed.columnHint === 'Column not found';
    html += `<div class="parse-preview-row"><span class="parse-label">Column</span><span class="parse-value${isError ? ' parse-error' : ''}">${e(parsed.columnHint)}</span></div>`;
  } else if (parsed.column) {
    const col = getColumnNames().find(c => c.id === parsed.column);
    if (col) html += `<div class="parse-preview-row"><span class="parse-label">Column</span><span class="parse-value">${e(col.name)}</span></div>`;
  }
  if (parsed.cue) {
    html += `<div class="parse-preview-row"><span class="parse-label">Cue</span><span class="parse-value">\ud83d\udd37 Quick action</span></div>`;
  }
  if (parsed.tasks && parsed.tasks.length > 0) {
    html += `<div class="parse-preview-row"><span class="parse-label">Tasks</span><span class="parse-value">${parsed.tasks.map(t => e(t)).join(', ')}</span></div>`;
  }
  const confClass = parsed.confidence === 'low' ? 'parse-confidence-low' : parsed.confidence === 'high' ? 'parse-confidence-high' : '';
  const aiName = _AI_NAME;
  if (parsed.confidence === 'low') {
    html += `<div class="parse-preview-row parse-confidence ${confClass}">${confidenceGlyph('low')} ${e(aiName)} will refine \u2197</div>`;
  } else {
    html += `<div class="parse-preview-row parse-confidence ${confClass}">${confidenceGlyph(parsed.confidence)} ${parsed.confidence}</div>`;
  }
  preview.innerHTML = html;
}

function fireCapParse(text) {
  if (_captureCapParseId) { clearInterval(_captureCapPollTimer); _captureCapParseId = null; }
  _capParseAttempts = 0;
  _capParseRequestText = text;
  fetch('/api/v1/parse-capture', {
    method: 'POST',
    headers: { ..._getAuthHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ rawText: text }),
  })
    .then(r => r.json())
    .then(data => {
      if (data.parseId) {
        _captureCapParseId = data.parseId;
        _captureCapPollTimer = setInterval(() => pollCapParse(), 4000);
      }
    })
    .catch(err => console.warn('[parse-capture] POST failed:', err.message));
}

function pollCapParse() {
  const aiName = _AI_NAME;
  if (!_captureCapParseId) return;
  if (++_capParseAttempts > MAX_PARSE_ATTEMPTS) {
    clearInterval(_captureCapPollTimer);
    _captureCapParseId = null;
    _captureParseResult = null;
    const hint = document.getElementById('capture-parse-hint');
    if (hint) hint.textContent = `${aiName} timed out \u2014 using local parse result.`;
    return;
  }
  fetch(`/api/v1/parse-capture?parseId=${encodeURIComponent(_captureCapParseId)}`, {
    headers: _getAuthHeader(),
  })
    .then(r => r.json())
    .then(data => {
      if (data.status === 'ready' && data.result) {
        clearInterval(_captureCapPollTimer);
        _captureCapParseId = null;
        // Race condition guard: discard stale response if input changed
        const currentInputText = document.getElementById('capture-quick-input')?.value?.trim() || '';
        if (currentInputText !== _capParseRequestText) return;
        _captureParseResult = data.result;
        renderParsePreview(data.result);
      } else if (data.status === 'failed') {
        clearInterval(_captureCapPollTimer);
        _captureCapParseId = null;
      }
    })
    .catch((err) => { console.warn('[Re:Focus] Capture parse poll failed:', err.message || err); });
}

function switchCaptureTab(tab) {
  document.querySelectorAll('.capture-tab').forEach(btn => {
    btn.classList.toggle('capture-tab-active', btn.dataset.tab === tab);
  });
  document.getElementById('capture-quick-pane').classList.toggle('hidden', tab !== 'quick');
  document.getElementById('capture-detailed-pane').classList.toggle('hidden', tab !== 'detailed');
  if (tab === 'quick') {
    document.getElementById('capture-quick-input').focus();
  } else {
    document.getElementById('capture-task').focus();
  }
}

export function openCapture() {
  const boardData = _getBoardData();
  if (!boardData) return;
  _focusTrapTriggerRef.value = document.activeElement;
  const modal = document.getElementById('capture-modal');
  const colSel = document.getElementById('capture-column');
  colSel.innerHTML = '';
  const cols = getColumnNames();
  for (const col of cols) {
    const opt = document.createElement('option');
    opt.value = col.id; opt.textContent = col.name;
    colSel.appendChild(opt);
  }
  document.getElementById('capture-name').value = '';
  document.getElementById('capture-task').value = '';
  document.getElementById('capture-quick-input').value = '';
  document.getElementById('capture-parse-preview').classList.add('hidden');
  document.getElementById('capture-parse-preview').innerHTML = '';
  document.getElementById('capture-parse-hint').classList.remove('hidden');
  _captureParseResult = null;
  _captureRipe = false;
  const ripeToggle = document.getElementById('capture-ripe-toggle');
  if (ripeToggle) ripeToggle.style.opacity = '0.3';
  _captureCapParseId = null;
  clearInterval(_captureCapPollTimer);
  clearTimeout(_captureCapFireTimer);
  clearTimeout(_captureParseTimer);
  // Default to Quick tab
  switchCaptureTab('quick');
  modal.classList.remove('hidden');
  document.getElementById('capture-quick-input').focus();
  if (_activeFocusTrapRef.value) _activeFocusTrapRef.value();
  _activeFocusTrapRef.value = _trapFocus(modal);
}

function closeCapture() {
  document.getElementById('capture-modal').classList.add('hidden');
  clearInterval(_captureCapPollTimer);
  clearTimeout(_captureCapFireTimer);
  clearTimeout(_captureParseTimer);
  _captureCapParseId = null;
  if (_activeFocusTrapRef.value) { _activeFocusTrapRef.value(); _activeFocusTrapRef.value = null; }
  if (_focusTrapTriggerRef.value) { _focusTrapTriggerRef.value.focus(); _focusTrapTriggerRef.value = null; }
}

function submitFromQuickTab() {
  const text = document.getElementById('capture-quick-input').value.trim();
  if (!text) return;
  const parsed = _captureParseResult || parseNaturalInput(text);

  // Pre-fill Detailed tab fields
  const colSel = document.getElementById('capture-column');
  if (parsed.column) colSel.value = parsed.column;

  const taskField = document.getElementById('capture-task');
  const nameField = document.getElementById('capture-name');

  if (parsed.taskText) {
    const taskWithCtx = parsed.context ? parsed.taskText + ' ' + parsed.context : parsed.taskText;
    taskField.value = taskWithCtx;
    nameField.value = parsed.cardName || '';
  } else if (parsed.cardName) {
    nameField.value = parsed.cardName;
    taskField.value = '';
  }

  // If high confidence, submit immediately
  if (parsed.confidence === 'high') {
    submitCapture(parsed);
  } else {
    // Switch to Detailed tab pre-filled for review
    switchCaptureTab('detailed');
  }
}

function submitCapture(parsedOverride) {
  const taskText = document.getElementById('capture-task').value.trim();
  const name = document.getElementById('capture-name').value.trim() || taskText.replace(/@(?:quick|cue)\b/gi, '').replace(/\bquick\b/gi, '').replace(/\s{2,}/g, ' ').trim();
  if (!taskText && !name) { document.getElementById('capture-task').focus(); return; }
  const colId = document.getElementById('capture-column').value;
  const projId = 'capture-' + Date.now();
  const ov2 = _loadOverrides();
  if (!ov2.addedProjects) ov2.addedProjects = [];

  // Use parsed dueDate if available
  const dueDate = (parsedOverride && parsedOverride.dueDate) || null;

  // Strip cue triggers from task text for clean display
  const cleanCueTriggers = (t) => t.replace(/@(?:quick|cue)\b/gi, '').replace(/\bquick\b/gi, '').replace(/\s{2,}/g, ' ').trim();
  const cleanTaskText = cleanCueTriggers(taskText);

  // Build tasks array — support multi-task from "with tasks:" parsing
  const tasks = [];
  if (parsedOverride && parsedOverride.tasks && parsedOverride.tasks.length > 0) {
    parsedOverride.tasks.forEach((t, i) => {
      tasks.push({ id: projId + '-t' + (i + 1), text: cleanCueTriggers(t), done: false, dueDate: null });
    });
  } else if (cleanTaskText) {
    tasks.push({ id: projId + '-t1', text: cleanTaskText, done: false, dueDate });
  }

  const newCard = {
    id: projId, name, color: PRESET_COLORS[ov2.addedProjects.length % PRESET_COLORS.length],
    createdAt: new Date().toISOString().split('T')[0], columnId: colId,
    tasks, status: '', outcome: '', contacts: [], comms: []
  };
  ov2.addedProjects.push(newCard);
  // Save cue (ripe) flag on tasks — from toggle or @quick/@cue/quick trigger
  const hasCueTrigger = _captureRipe || (parsedOverride && parsedOverride.cue) || /@(?:quick|cue)\b/i.test(taskText) || /\bquick\b/i.test(taskText);
  if (hasCueTrigger && tasks.length > 0) {
    if (!ov2.taskRipe) ov2.taskRipe = {};
    for (const t of tasks) ov2.taskRipe[t.id] = true;
    _captureRipe = false;
  }
  _saveOverrides(ov2);

  // Enqueue @delegate tasks
  if (/@delegate\b/i.test(taskText)) {
    fetch('/api/v1/delegate', {
      method: 'POST',
      headers: { ..._getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cardId: projId,
        cardName: name,
        taskId: projId + '-t1',
        taskText: taskText,
        cardScope: '',
        cardNotes: '',
      }),
    }).catch(err => console.warn('[delegate] enqueue failed:', err.message));
  }

  closeCapture();
  _renderFromState();
}

// AI Ripe suggestion hook — stub for future Cap integration
// Returns tasks on a card that are likely "ripe" (short, actionable, no blockers)
export function suggestRipeTasks(card) {
  if (!card || !card.tasks) return [];
  return card.tasks.filter(t =>
    !t.done &&
    t.text && t.text.length < 80 &&
    !/@waiting\b/i.test(t.text)
  );
}

export function setupCaptureListeners() {
  // Ripe toggle on capture detailed pane
  const captureRipeToggle = document.getElementById('capture-ripe-toggle');
  if (captureRipeToggle) {
    captureRipeToggle.addEventListener('click', () => {
      _captureRipe = !_captureRipe;
      captureRipeToggle.style.opacity = _captureRipe ? '1' : '0.3';
    });
  }

  // Tab buttons
  document.querySelectorAll('.capture-tab').forEach(btn => {
    btn.addEventListener('click', () => switchCaptureTab(btn.dataset.tab));
  });

  // Quick input — debounced parse + preview
  document.getElementById('capture-quick-input').addEventListener('input', () => {
    clearTimeout(_captureParseTimer);
    clearTimeout(_captureCapFireTimer);
    _captureParseTimer = setTimeout(() => {
      const text = document.getElementById('capture-quick-input').value.trim();
      if (!text) {
        _captureParseResult = null;
        renderParsePreview(null);
        return;
      }
      const parsed = parseNaturalInput(text);
      _captureParseResult = parsed;
      renderParsePreview(parsed);

      // If low confidence and text long enough, fire Cap parse after 1s
      if (parsed.confidence === 'low' && text.length > 10) {
        _captureCapFireTimer = setTimeout(() => fireCapParse(text), 1000);
      }
    }, 400);
  });

  // Quick input — Enter to submit, Escape to close
  document.getElementById('capture-quick-input').addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); submitFromQuickTab(); }
    if (ev.key === 'Escape') closeCapture();
  });

  document.getElementById('capture-btn').addEventListener('click', openCapture);
  document.getElementById('capture-submit').addEventListener('click', () => {
    const quickPane = document.getElementById('capture-quick-pane');
    if (!quickPane.classList.contains('hidden')) {
      submitFromQuickTab();
    } else {
      submitCapture();
    }
  });
  document.getElementById('capture-cancel').addEventListener('click', closeCapture);
  document.getElementById('capture-modal').addEventListener('click', (ev) => {
    if (ev.target.id === 'capture-modal') closeCapture();
  });
  // Tab/Enter flow in Detailed tab: task → card title → column → submit
  document.getElementById('capture-task').addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' || ev.key === 'Tab') { ev.preventDefault(); document.getElementById('capture-name').focus(); }
    if (ev.key === 'Escape') closeCapture();
  });
  document.getElementById('capture-name').addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' || ev.key === 'Tab') { ev.preventDefault(); document.getElementById('capture-column').focus(); }
    if (ev.key === 'Escape') closeCapture();
  });
  document.getElementById('capture-column').addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') { ev.preventDefault(); submitCapture(); }
    if (ev.key === 'Escape') closeCapture();
  });
}
